import { useState, useCallback, useEffect, useRef } from "react";
import { useApi } from "../../../../../../api/ApiProvider";
import { ValidityDto } from "../../../../../../api-client";
import { GkvBillEditDto } from "../types/interface";
import { VALIDATION_CHUNK_SIZE, MAX_PARALLEL_VALIDATIONS } from "../../GKVNew/params/constants";

/** Two-level map: Rx ID → validator name → ValidityDto */
export type ValidationMap = Record<string, Record<string, ValidityDto>>;

export interface ValidationProgress {
  completed: number;
  total: number;
}

export interface UseEditValidationReturn {
  validationMap: ValidationMap;
  isValidating: boolean;
  validationProgress: ValidationProgress;
}

/**
 * Progressive chunked validation for the GKV Edit flow.
 *
 * Reuses the same batching pattern as GKVNew's useGkvBills:
 * - Splits Rx IDs into chunks of VALIDATION_CHUNK_SIZE
 * - Runs up to MAX_PARALLEL_VALIDATIONS concurrent API calls
 * - Merges results progressively so the UI updates as each chunk completes
 * - Abortable on unmount or when bills change
 *
 * User can freely interact (taxieren, add/remove Rx) while validation runs.
 */
export function useEditValidation(bills: GkvBillEditDto[]): UseEditValidationReturn {
  const [validationMap, setValidationMap] = useState<ValidationMap>({});
  const [isValidating, setIsValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState<ValidationProgress>({
    completed: 0,
    total: 0,
  });

  const { transaction } = useApi();
  const abortControllerRef = useRef<AbortController | null>(null);
  const hasValidatedRef = useRef(false);

  /**
   * Validates Rx IDs in parallel chunks.
   * Merges results progressively into validationMap as each chunk completes.
   */
  const validateInChunks = useCallback(
    async (rxIds: string[]) => {
      if (rxIds.length === 0) return;

      // Abort any previous in-flight validation
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsValidating(true);
      setValidationProgress({ completed: 0, total: rxIds.length });

      // Split into chunks
      const chunks: string[][] = [];
      for (let i = 0; i < rxIds.length; i += VALIDATION_CHUNK_SIZE) {
        chunks.push(rxIds.slice(i, i + VALIDATION_CHUNK_SIZE));
      }

      // Worker pool with shared cursor
      let index = 0;
      const runWorker = async () => {
        while (true) {
          const currentIndex = index++;
          if (currentIndex >= chunks.length) break;
          if (controller.signal.aborted) return;

          const chunk = chunks[currentIndex];
          try {
            const result = await transaction.validateGkvBillRxs(
              { rxIds: chunk },
              { signal: controller.signal },
            );
            if (!controller.signal.aborted) {
              setValidationMap((prev) => ({
                ...prev,
                ...(result as unknown as ValidationMap),
              }));
              setValidationProgress((prev) => ({
                ...prev,
                completed: prev.completed + chunk.length,
              }));
            }
          } catch (error: any) {
            if (error?.name === "CanceledError" || controller.signal.aborted) return;
            console.error("Validation chunk failed:", error);
          }
        }
      };

      const workerCount = Math.min(MAX_PARALLEL_VALIDATIONS, chunks.length);
      const workers = Array.from({ length: workerCount }, () => runWorker());
      await Promise.all(workers);

      if (!controller.signal.aborted) {
        setIsValidating(false);
      }
    },
    [transaction],
  );

  // Kick off validation once when bills arrive
  useEffect(() => {
    if (bills.length > 0 && !hasValidatedRef.current) {
      hasValidatedRef.current = true;
      const allRxIds = bills.flatMap((b) => (b.rxs ?? []).map((rx) => rx.id));
      validateInChunks(allRxIds);
    }
  }, [bills, validateInChunks]);

  // Cleanup: abort on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return { validationMap, isValidating, validationProgress };
}
